import React from "react";
import { motion } from "framer-motion";
import { CheckCircle, ChevronRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const PricingSection = () => {
	const { t } = useTranslation();
	const primaryColor = "#43bd98";
	const isMobile = useMediaQuery("(max-width: 768px)");

	const features = t("pricingRestaurantNew.features", { returnObjects: true });

	const listVariants = {
		hidden: { opacity: 0 },
		show: {
			opacity: 1,
			transition: { staggerChildren: 0.08 },
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, x: -15 },
		show: { opacity: 1, x: 0, transition: { duration: 0.3 } },
	};

	return (
		<section
			id="precios"
			style={{
				backgroundColor: "#f8fefc",
				padding: isMobile ? "4rem 1.5rem" : "5rem 2rem",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div style={{ maxWidth: "600px", margin: "0 auto" }}>
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7 }}
					viewport={{ once: true }}
					style={{ textAlign: "center", marginBottom: "2.5rem" }}
				>
					<p
						style={{
							color: primaryColor,
							fontSize: "0.9rem",
							fontWeight: 600,
							letterSpacing: "1px",
							textTransform: "uppercase",
							marginBottom: "0.5rem",
						}}
					>
						{t("pricingRestaurantNew.badge")}
					</p>
					<h2
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
							color: "#1a1a1a",
							fontWeight: 800,
							margin: 0,
						}}
					>
						{t("pricingRestaurantNew.title")}
					</h2>
					<p
						style={{
							fontSize: "1rem",
							color: "#6b7280",
							marginTop: "0.75rem",
						}}
					>
						{t("pricingRestaurantNew.subtitle")}
					</p>
				</motion.div>

				{/* Pricing Card */}
				<motion.div
					initial={{ opacity: 0, scale: 0.98 }}
					whileInView={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
					style={{
						backgroundColor: "#fff",
						borderRadius: "24px",
						boxShadow: "0 12px 40px rgba(0,0,0,0.08)",
						padding: isMobile ? "2rem 1.5rem" : "2.5rem",
						border: `2px solid ${primaryColor}`,
						position: "relative",
						overflow: "hidden",
					}}
				>
					{/* Ribbon */}
					<div
						style={{
							position: "absolute",
							top: "20px",
							right: "-35px",
							background: primaryColor,
							color: "#fff",
							padding: "6px 40px",
							fontSize: "0.75rem",
							fontWeight: 600,
							transform: "rotate(45deg)",
						}}
					>
						{t("pricingRestaurantNew.ribbon")}
					</div>

					{/* Plan title */}
					<div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
						<h3
							style={{
								fontSize: "1.2rem",
								fontWeight: 700,
								color: "#1f2937",
								marginBottom: "0.25rem",
							}}
						>
							{t("pricingRestaurantNew.planTitle")}
						</h3>
						<p style={{ fontSize: "0.9rem", color: "#6b7280", margin: 0 }}>
							{t("pricingRestaurantNew.planSubtitle")}
						</p>
					</div>

					{/* Price */}
					<div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
						<del
							style={{
								color: "#9ca3af",
								fontSize: "1.1rem",
								display: "block",
								marginBottom: "4px",
							}}
						>
							{t("pricingRestaurantNew.oldPrice")}
						</del>
						<div
							style={{
								display: "flex",
								alignItems: "baseline",
								justifyContent: "center",
								gap: "4px",
							}}
						>
							<span
								style={{
									fontSize: "3.5rem",
									fontWeight: 800,
									color: primaryColor,
									lineHeight: 1,
								}}
							>
								{t("pricingRestaurantNew.price")}
							</span>
							<span
								style={{
									fontSize: "1.1rem",
									color: "#6b7280",
								}}
							>
								{t("pricingRestaurantNew.period")}
							</span>
						</div>
						<p
							style={{
								fontSize: "0.8rem",
								color: "#9ca3af",
								marginTop: "4px",
							}}
						>
							{t("pricingRestaurantNew.vatNote")}
						</p>
					</div>

					{/* Trial badge */}
					<div
						style={{
							background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}08 100%)`,
							border: `1px dashed ${primaryColor}`,
							borderRadius: "12px",
							padding: "14px 20px",
							textAlign: "center",
							marginBottom: "1.75rem",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "10px",
						}}
					>
						<Sparkles size={20} color={primaryColor} />
						<div>
							<p
								style={{
									fontSize: "1rem",
									fontWeight: 700,
									color: primaryColor,
									margin: 0,
								}}
							>
								{t("pricingRestaurantNew.trialBadge")}
							</p>
							<p
								style={{
									fontSize: "0.8rem",
									color: "#6b7280",
									margin: 0,
								}}
							>
								{t("pricingRestaurantNew.trialText")}
							</p>
						</div>
					</div>

					{/* Features title */}
					<p
						style={{
							fontSize: "0.85rem",
							fontWeight: 600,
							color: "#9ca3af",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
							marginBottom: "1rem",
						}}
					>
						{t("pricingRestaurantNew.featuresTitle")}
					</p>

					{/* Features */}
					<motion.ul
						variants={listVariants}
						initial="hidden"
						whileInView="show"
						viewport={{ once: true }}
						style={{
							padding: 0,
							margin: 0,
							marginBottom: "1.75rem",
							listStyle: "none",
							display: "grid",
							gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
							gap: "0.6rem 1.5rem",
						}}
					>
						{features.map((item, idx) => (
							<motion.li
								key={idx}
								variants={itemVariants}
								style={{
									display: "flex",
									alignItems: "flex-start",
									fontSize: "0.9rem",
									color: "#374151",
									lineHeight: 1.5,
								}}
							>
								<CheckCircle
									size={17}
									strokeWidth={2.5}
									style={{
										color: primaryColor,
										flexShrink: 0,
										marginRight: "8px",
										marginTop: "2px",
									}}
								/>
								{item}
							</motion.li>
						))}
					</motion.ul>

					{/* CTA */}
					<motion.a
						href="https://creator.applaby.com/?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
						target="_blank"
						rel="noreferrer"
						whileHover={{ scale: 1.02 }}
						whileTap={{ scale: 0.98 }}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "8px",
							width: "100%",
							background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
							color: "#fff",
							padding: "1rem 1.5rem",
							borderRadius: "12px",
							textDecoration: "none",
							fontWeight: 600,
							fontSize: "0.95rem",
							boxShadow: "0 8px 20px rgba(67,189,152,0.25)",
						}}
					>
						{t("pricingRestaurantNew.cta")} <ChevronRight size={18} />
					</motion.a>
				</motion.div>
			</div>
		</section>
	);
};

export default PricingSection;
